import { Checklist } from "./Checklist";
import { CompletionBar } from "./CompletionBar";

const TAB = "Recueil Op\u00e9rationnel";

const ChevronDown = ({ className = "" }) => (
  <svg fill="none" height="24" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="24" className={className}><path d="M19 9l-7 7-7-7" /></svg>
);
const ChevronDownSm = ({ className = "" }) => (
  <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16" className={className}><path d="M19 9l-7 7-7-7" /></svg>
);

function Accordion({ title, children, defaultOpen = false }) {
  return (
    <details className="group bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm" open={defaultOpen || undefined}>
      <summary className="flex justify-between items-center font-bold cursor-pointer p-6 text-left text-gray-900 dark:text-white hover:bg-[#f4f7f4] dark:hover:bg-[#1f2620] rounded-xl">
        <span className="text-lg">{title}</span>
        <span className="transition group-open:rotate-180 text-[#4c7e55] dark:text-[#2e6d3b] shrink-0 ml-4"><ChevronDown /></span>
      </summary>
      <div className="p-6 border-t border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
        {children}
      </div>
    </details>
  );
}

function SubAccordion({ title, children }) {
  return (
    <details className="group/sub bg-gray-50 dark:bg-[#242424] border border-gray-200 dark:border-gray-700 rounded-lg">
      <summary className="flex justify-between items-center font-semibold cursor-pointer p-4 text-left text-sm text-gray-800 dark:text-gray-200 hover:text-[#4c7e55] dark:hover:text-[#2e6d3b] transition-colors">
        <span>{title}</span>
        <span className="transition group-open/sub:rotate-180 shrink-0 ml-4"><ChevronDownSm /></span>
      </summary>
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300">
        {children}
      </div>
    </details>
  );
}

function SectionTitle({ children }) {
  return <h5 className="font-bold text-gray-900 dark:text-white mt-3 mb-1 text-xs uppercase tracking-wide">{children}</h5>;
}

export default function RecueilOperationnel() {
  return (
    <div className="space-y-6 pb-10" data-testid="recueil-operationnel">
      <CompletionBar tabName="Recueil Op\u00e9rationnel" />
      <div className="mb-8">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
          {"Recueil exhaustif des obligations et flux op\u00e9rationnels"}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          {"SA genevoise ~20 salari\u00e9es \u2014 Exercice au 31.12 \u2014 TVA mensuelle \u2014 R\u00e9vision restreinte"}
        </p>
      </div>

      <Accordion title={"R\u00e9sum\u00e9 ex\u00e9cutif & Feuille de mission DAF"}>
        <p className="mb-4">{"Une SA genevoise d\u2019environ "}<strong>{"20 salari\u00e9es"}</strong>{" vit au rythme de trois moteurs de conformit\u00e9\u00a0: "}<strong>{"(1) paie/social/imp\u00f4t \u00e0 la source"}</strong>{", "}<strong>{"(2) TVA"}</strong>{", "}<strong>{"(3) cl\u00f4ture/r\u00e9vision/imp\u00f4t PM et gouvernance"}</strong>{". \u00c0 20 salari\u00e9es, ces trois moteurs doivent \u00eatre "}<strong>{"industrialis\u00e9s"}</strong>{" (process, cut-off, contr\u00f4les, archivage)."}</p>
        <h4 className="font-bold text-gray-900 dark:text-white mb-2 mt-6">{"\u00c9ch\u00e9ances structurantes \u00ab\u00a0non n\u00e9gociables\u00a0\u00bb"}</h4>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li><strong>{"Assembl\u00e9e g\u00e9n\u00e9rale ordinaire"}</strong>{"\u00a0: dans les 6 mois suivant la cl\u00f4ture (exercice 31.12 \u2192 AG au plus tard 30.06)."}</li>
          <li><strong>{"Convocation AG"}</strong>{"\u00a0: \u2265\u00a020 jours avant la date. Documents accessibles \u2265\u00a020 jours avant l\u2019AG."}</li>
          <li><strong>{"Imp\u00f4t \u00e0 la source (Gen\u00e8ve)"}</strong>{"\u00a0: transmission et reversement mensuels "}<strong>{"dans les 30 jours"}</strong>{" suivant le versement. Bouclement annuel "}<strong>{"au plus tard le 31 janvier"}</strong>{"."}</li>
          <li><strong>{"30 janvier (N+1)"}</strong>{"\u00a0: d\u00e9claration annuelle des salaires AVS/AI/APG/AC (OCAS). Int\u00e9r\u00eat moratoire 5\u00a0% si retard."}</li>
          <li><strong>{"TVA"}</strong>{"\u00a0: d\u00e9compte et paiement dans les "}<strong>{"60 jours"}</strong>{". Corrections\u00a0: d\u00e9compte rectificatif \u2264\u00a0"}<strong>{"180 jours"}</strong>{" apr\u00e8s fin d\u2019exercice."}</li>
          <li><strong>{"D\u00e9claration fiscale PM (Gen\u00e8ve)"}</strong>{"\u00a0: d\u00e9p\u00f4t normalement avant le "}<strong>{"30 avril"}</strong>{" (prolongation payante possible)."}</li>
          <li><strong>{"Imp\u00f4t anticip\u00e9 (dividendes)"}</strong>{"\u00a0: formulaires \u2264\u00a0"}<strong>{"30 jours"}</strong>{" apr\u00e8s \u00e9ch\u00e9ance de la prestation imposable."}</li>
        </ul>
        <h4 className="font-bold text-gray-900 dark:text-white mb-2 mt-6">{"Corrections majeures par rapport aux documents internes"}</h4>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>{"La "}<strong>{"taxe professionnelle communale (TPC)"}</strong>{" est "}<strong>{"supprim\u00e9e d\u00e8s le 01.01.2024"}</strong>{"."}</li>
          <li>{"Le "}<strong>{"reversement IS"}</strong>{" (Gen\u00e8ve) est "}<strong>{"mensuel dans les 30 jours"}</strong>{" suivant le versement du salaire (pas \u00ab\u00a0le 15\u00a0\u00bb)."}</li>
          <li>{"Les "}<strong>{"taux TVA"}</strong>{" corrects\u00a0: "}<strong>{"8,1\u00a0% / 2,6\u00a0% / 3,8\u00a0% (h\u00e9bergement)"}</strong>{"."}</li>
        </ul>
        <h4 className="font-bold text-gray-900 dark:text-white mb-2 mt-6">{"Point de vigilance logiciel"}</h4>
        <p className="mb-4 p-3 bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.18)] rounded-lg border border-[#bfd1c3] dark:border-[#2e6d3b] text-[#3f6947] dark:text-[#4d8a5b]">
          <strong>{"A3 Salaire et Finance"}</strong>{"\u00a0: l\u2019\u00e9diteur communique sur ELM\u00a05.0, or Gen\u00e8ve exige "}<strong>{"ELM\u00a05.3+"}</strong>{". "}<strong>{"Action imm\u00e9diate"}</strong>{"\u00a0: v\u00e9rifier version ELM effective + certification Swissdec ELM\u00a05.3."}
        </p>
        <h4 className="font-bold text-gray-900 dark:text-white mb-2 mt-6">{"Feuille de mission DAF en une phrase"}</h4>
        <p className="mb-4 text-[#4c7e55] dark:text-[#2e6d3b] font-medium p-4 bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.18)] rounded-lg border border-[#bfd1c3] dark:border-[#2e6d3b]">
          {"Outiller la paie (Swissdec/ELM\u00a05.3), verrouiller la piste bancaire (pain.001/camt + double validation), cadencer les \u00e9ch\u00e9ances Gen\u00e8ve (IS, OCAS, PM, acomptes), et rendre \u00ab\u00a0audit-proof\u00a0\u00bb l\u2019archivage (10 ans)."}
        </p>
      </Accordion>

      <Accordion title={"P\u00e9rim\u00e8tre, hypoth\u00e8ses et variables"}>
        <h4 className="font-bold text-gray-900 dark:text-white mb-2">{"Hypoth\u00e8ses de base"}</h4>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>{"Forme\u00a0: "}<strong>{"SA"}</strong>{"\u00a0; si\u00e8ge\u00a0: canton de "}<strong>{"Gen\u00e8ve"}</strong>{"."}</li>
          <li>{"Exercice comptable = "}<strong>{"ann\u00e9e civile"}</strong>{" (cl\u00f4ture "}<strong>{"31.12.N"}</strong>{")."}</li>
          <li>{"Effectif\u00a0: "}<strong>{"~20 salari\u00e9es"}</strong>{"."}</li>
          <li>{"SA non cot\u00e9e, structure \u00ab\u00a0standard\u00a0\u00bb."}</li>
          <li>{"TVA\u00a0: assujetti "}<strong>{"OUI"}</strong>{", d\u00e9comptes "}<strong>{"mensuels"}</strong>{"."}</li>
          <li>{"Paie sur "}<strong>{"A3"}</strong>{"."}</li>
          <li>{"R\u00e9vision "}<strong>{"restreinte"}</strong>{" (opting-out impossible \u00e0 ~20 salari\u00e9es, r\u00e9serve \u226410\u00a0EPT)."}</li>
        </ul>
        <h4 className="font-bold text-gray-900 dark:text-white mb-2 mt-4">{"Variables qui modifient les flux"}</h4>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li><strong>{"Seuils d\u2019audit"}</strong>{"\u00a0: contr\u00f4le ordinaire vs restreint (bilan 20\u00a0M / CA 40\u00a0M / 250\u00a0EPT)."}</li>
          <li><strong>{"TVA"}</strong>{"\u00a0: m\u00e9thode (effective vs TDFN), op\u00e9rations exon\u00e9r\u00e9es, prestations \u00e0 l\u2019\u00e9tranger."}</li>
          <li><strong>{"Distribution de dividendes"}</strong>{"\u00a0: d\u00e9clenche l\u2019imp\u00f4t anticip\u00e9."}</li>
          <li><strong>{"Secteurs r\u00e9glement\u00e9s"}</strong>{"\u00a0: mati\u00e8res premi\u00e8res, cha\u00eene d\u2019approvisionnement (ODiTr), CCT."}</li>
          <li><strong>{"Donn\u00e9es personnelles / LPD"}</strong>{"\u00a0: obligations proportionnelles au risque."}</li>
        </ul>
      </Accordion>

      <Accordion title={"Contr\u00f4le critique des documents internes"}>
        <h4 className="font-bold text-green-700 dark:text-green-400 mb-2">{"Ce qui est juste (\u00e0 conserver)"}</h4>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>{"AG dans les 6 mois\u00a0: conforme au CO."}</li>
          <li>{"Opting-out sous conditions (<10\u00a0EPT + unanimit\u00e9)\u00a0: conforme."}</li>
          <li>{"Archivage 10 ans des pi\u00e8ces\u00a0: conforme."}</li>
        </ul>
        <h4 className="font-bold text-red-700 dark:text-red-400 mb-2 mt-4">{"Erreurs ou obsolescences nettes"}</h4>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li><strong>{"TPC"}</strong>{"\u00a0: pr\u00e9sent\u00e9e comme obligation possible \u2192 en 2026, elle est "}<strong>{"abolie"}</strong>{" (01.01.2024)."}</li>
          <li><strong>{"Imp\u00f4t \u00e0 la source \u00ab\u00a0le 15\u00a0\u00bb"}</strong>{"\u00a0: Gen\u00e8ve fonctionne sur "}<strong>{"30 jours apr\u00e8s versement"}</strong>{"."}</li>
          <li><strong>{"TVA\u00a0: qualification des taux"}</strong>{"\u00a0: les taux sont 8,1 / 2,6 / 3,8 (h\u00e9bergement). Le 3,8\u00a0% \u2260 biens essentiels."}</li>
        </ul>
        <h4 className="font-bold text-[#4c7e55] dark:text-[#4d8a5b] mb-2 mt-4">{"Ce qui manque dans les docs"}</h4>
        <ul className="list-disc pl-5 mb-0 space-y-1">
          <li>{"Redevance radio-TV entreprises (SEDA/Serafe) li\u00e9e au CA TVA."}</li>
          <li>{"Imp\u00f4t anticip\u00e9\u00a0: obligation \u00e0 30 jours apr\u00e8s approbation des comptes."}</li>
          <li>{"ODiTr\u00a0: module \u00ab\u00a0diligence & reporting\u00a0\u00bb conditionnel."}</li>
        </ul>
      </Accordion>

      <Accordion title={"Recueil op\u00e9rationnel par flux \u2014 Fiches d\u00e9taill\u00e9es"} defaultOpen>
        <div className="space-y-4">
          <FluxA /><FluxB /><FluxC /><FluxD /><FluxE /><FluxF /><FluxG /><FluxH />
          <FluxI /><FluxJ /><FluxK /><FluxL /><FluxM /><FluxN /><FluxO /><FluxP />
          <FluxQ /><FluxR /><FluxS /><FluxT /><FluxV /><FluxW /><FluxX />
        </div>
      </Accordion>

      <Accordion title={"Feuille de mission"}>
        <h4 className="font-bold text-gray-900 dark:text-white mb-2">{"Priorit\u00e9s concr\u00e8tes (30-60 prochains jours)"}</h4>
        <div className="p-3 mb-4 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-200 dark:border-red-900/30">
          <p className="text-red-800 dark:text-red-300"><strong>{"Priorit\u00e9 imm\u00e9diate\u00a0: conformit\u00e9 ELM"}</strong></p>
          <p className="text-red-700 dark:text-red-400 mt-1">{"V\u00e9rifier la capacit\u00e9 du stack A3 \u00e0 produire/transmettre "}<strong>{"ELM\u00a05.3"}</strong>{". Action\u00a0: ticket \u00e9diteur/int\u00e9grateur + demande attestation \u00ab\u00a0certifi\u00e9 Swissdec ELM\u00a05.3\u00a0\u00bb + test de bout en bout."}</p>
        </div>
        <div className="p-3 mb-4 bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.18)] rounded-lg border border-[#bfd1c3] dark:border-[#2e6d3b]">
          <p className="text-[#3f6947] dark:text-[#2e6d3b]"><strong>{"Priorit\u00e9 \u00ab\u00a0hygi\u00e8ne financi\u00e8re\u00a0\u00bb"}</strong></p>
          <p className="text-[#4c7e55] dark:text-[#2e6d3b] mt-1">{"Industrialiser le "}<strong>{"cut-off mensuel"}</strong>{" (paie, IS, TVA, banque). Standardiser la "}<strong>{"piste d\u2019audit"}</strong>{" (PBC list, archivage 10 ans)."}</p>
        </div>
        <h4 className="font-bold text-gray-900 dark:text-white mb-2 mt-4">{"Noyau interne (\u00e0 garder en maison)"}</h4>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>{"Pilotage calendrier l\u00e9gal (IS, OCAS, TVA, PM, AG)."}</li>
          <li>{"Tr\u00e9sorerie (paiements + rapprochements) et contr\u00f4le interne."}</li>
          <li>{"Reporting solvabilit\u00e9 au CA (CO\u00a0725/725a/725b)."}</li>
        </ul>
        <h4 className="font-bold text-gray-900 dark:text-white mb-2 mt-4">{"\u00c0 d\u00e9l\u00e9guer (efficace \u00e0 ~20)"}</h4>
        <ul className="list-disc pl-5 mb-4 space-y-1">
          <li>{"Paie compl\u00e8te + Swissdec/ELM + d\u00e9claratifs."}</li>
          <li>{"TVA et dossier fiscal PM si complexit\u00e9."}</li>
          <li>{"R\u00e9vision et parfois secr\u00e9tariat juridique."}</li>
          <li>{"LPD\u00a0: appui sp\u00e9cialis\u00e9 (registre, AIPD, clauses sous-traitants)."}</li>
        </ul>
        <h4 className="font-bold text-gray-900 dark:text-white mb-2 mt-4">{"Outils minimaux \u00e0 s\u00e9curiser"}</h4>
        <ul className="list-disc pl-5 mb-0 space-y-1">
          <li><strong>{"Paie"}</strong>{"\u00a0: logiciel certifi\u00e9 ELM/Swissdec (clarifier trajectoire A3 \u2192 ELM\u00a05.3)."}</li>
          <li><strong>{"Banque"}</strong>{"\u00a0: capacit\u00e9 pain.001 + camt.* + workflow double signature."}</li>
          <li><strong>{"GED"}</strong>{"\u00a0: archivage 10 ans \u00ab\u00a0audit-proof\u00a0\u00bb (PDF/A + contr\u00f4le d\u2019acc\u00e8s)."}</li>
          <li><strong>{"Calendrier conformit\u00e9"}</strong>{"\u00a0: agenda partag\u00e9 (mensuel/annuel) + tableau des preuves."}</li>
        </ul>
      </Accordion>

      <Accordion title={"Checklists synth\u00e9tiques"}>
        <h4 className="font-bold text-gray-900 dark:text-white mb-3">{"Checklist annuelle de conformit\u00e9 (\u00ab\u00a0socle\u00a0\u00bb)"}</h4>
        <div className="space-y-3 mb-6">
          <div className="p-3 bg-gray-50 dark:bg-[#242424] rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="font-bold text-gray-900 dark:text-white mb-1">Janvier (N)</p>
            <Checklist tab={TAB} idPrefix="synth-jan" items={[
              "Boucler l\u2019ann\u00e9e N-1 (paie + compta) et pr\u00e9parer certificats de salaire.",
              "D\u00e9claration OCAS salaires N-1 &le;&nbsp;30 janvier.",
              "Solde imp\u00f4t \u00e0 la source N-1 + attestation-quittance &le;&nbsp;31 janvier.",
            ]} />
          </div>
          <div className="p-3 bg-gray-50 dark:bg-[#242424] rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="font-bold text-gray-900 dark:text-white mb-1">{"F\u00e9vrier\u2013avril (N)"}</p>
            <Checklist tab={TAB} idPrefix="synth-fev" items={[
              "R\u00e9troplanning AG + comptes + r\u00e9vision.",
              "D\u00e9claration fiscale PM si cl\u00f4ture 31.12&nbsp;: &le;&nbsp;30 avril.",
            ]} />
          </div>
          <div className="p-3 bg-gray-50 dark:bg-[#242424] rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="font-bold text-gray-900 dark:text-white mb-1">{"Mai\u2013juin (N)"}</p>
            <Checklist tab={TAB} idPrefix="synth-mai" items={[
              "Pr\u00e9-AG (draft comptes, rapport de gestion).",
              "AG ordinaire (&le;&nbsp;6 mois) + convocation/dossier &ge;&nbsp;20 jours.",
            ]} />
          </div>
          <div className="p-3 bg-gray-50 dark:bg-[#242424] rounded-lg border border-gray-200 dark:border-gray-700">
            <p className="font-bold text-gray-900 dark:text-white mb-1">{"Toute l\u2019ann\u00e9e"}</p>
            <Checklist tab={TAB} idPrefix="synth-annual" items={[
              "Imp\u00f4t \u00e0 la source&nbsp;: reversement mensuel &le;&nbsp;30 jours apr\u00e8s paie.",
              "TVA mensuelle&nbsp;: d\u00e9compte &le;&nbsp;60 jours.",
              "Acomptes fiscaux PM&nbsp;: paiement selon cadence genevoise.",
              "Archivage 10 ans, registre LPD si applicable.",
            ]} />
          </div>
        </div>
        <h4 className="font-bold text-gray-900 dark:text-white mb-3">Checklist DAF mensuelle</h4>
        <Checklist tab={TAB} idPrefix="synth-mensuelle" items={[
          "Paie valid\u00e9e + paiements effectu\u00e9s&nbsp;; preuve bancaire archiv\u00e9e.",
          "Imp\u00f4t source revers\u00e9 &le;&nbsp;30 jours.",
          "Rapprochement banque (camt) fait.",
          "TVA mensuelle&nbsp;: d\u00e9compte + paiement &le;&nbsp;60 jours.",
          "Cl\u00f4ture mensuelle J+5 (banque, paie, TVA, cut-off).",
        ]} />
      </Accordion>

      <Accordion title={"\u00c0 v\u00e9rifier localement"}>
        <p className="mb-3">{"Les \u00ab\u00a0grandes zones grises\u00a0\u00bb o\u00f9 la bonne r\u00e9ponse d\u00e9pend du cas concret\u00a0:"}</p>
        <Checklist tab={TAB} idPrefix="verif-local" items={[
          "<strong>TVA</strong>&nbsp;: assujettissement confirm\u00e9, m\u00e9thode (effective vs TDFN), op\u00e9rations internationales.",
          "<strong>Effectif & r\u00e9vision</strong>&nbsp;: niveau de r\u00e9vision et exigences d\u2019organe de r\u00e9vision.",
          "<strong>Imp\u00f4t \u00e0 la source</strong>&nbsp;: part de personnel concern\u00e9, frontaliers France.",
          "<strong>Immigration</strong>&nbsp;: profils (UE/AELE vs \u00c9tats tiers), proc\u00e9dures cantonales, d\u00e9lais.",
          "<strong>Assurances (LAA/IJM/LPP)</strong>&nbsp;: assureur LAA exact, caisse LPP/plan.",
          "<strong>Protection des donn\u00e9es</strong>&nbsp;: registre des traitements requis ou non, process \u00ab&nbsp;incident&nbsp;\u00bb.",
          "<strong>Dividendes / imp\u00f4t anticip\u00e9</strong>&nbsp;: structure actionnariale (\u00e9trangers, groupe).",
          "<strong>Logiciel paie A3</strong>&nbsp;: ELM&nbsp;5.3 r\u00e9el et certification Swissdec (\u00e0 prouver).",
          "<strong>Convention(s) collective(s)</strong> potentielle(s) selon secteur.",
          "<strong>Politique interne de frais</strong> (plafonds, v\u00e9hicules, per diem).",
        ]} />
      </Accordion>
    </div>
  );
}

/* ==================== FLUX ==================== */

function FluxA() {
  return (
    <SubAccordion title={"Gouvernance SA (AG, docs, PV, d\u00e9cisions statutaires)"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" organisation du cycle de gouvernance\u00a0: pr\u00e9paration des documents, convocation, tenue de l\u2019assembl\u00e9e, r\u00e9daction du PV, ex\u00e9cution des d\u00e9cisions. L\u2019AG ordinaire doit se tenir "}<strong>{"dans les 6 mois"}</strong>{" suivant la cl\u00f4ture."}</p>
      <SectionTitle>{"R\u00e8gles & \u00e9ch\u00e9ances"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"CO art.\u00a0699-700\u00a0: convocation \u2265\u00a0"}<strong>{"20 jours"}</strong>{" avant la date."}</li>
        <li>{"Documents (rapport de gestion + rapport de r\u00e9vision) accessibles \u2265\u00a0"}<strong>{"20 jours"}</strong>{" avant l\u2019AG."}</li>
        <li>{"Op\u00e9rationnel recommand\u00e9\u00a0: pr\u00e9-cl\u00f4ture M+2, audit M+3, AG M+5."}</li>
      </ul>
      <SectionTitle>{"Proc\u00e9dure"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"T-10 semaines\u00a0: calendrier AG + kick-off cl\u00f4ture/r\u00e9vision."}</li>
        <li>{"T-6 semaines\u00a0: draft comptes + annexe + rapport de gestion."}</li>
        <li>{"T-4 semaines\u00a0: envoi PBC au r\u00e9viseur."}</li>
        <li>{"T-3 semaines\u00a0: convocation et finalisation dossiers."}</li>
        <li>{"T0\u00a0: AG + PV sign\u00e9."}</li>
        <li>{"T+5 jours\u00a0: ex\u00e9cution d\u00e9cisions (dividendes, d\u00e9p\u00f4ts RC)."}</li>
      </ul>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-a" items={[
        "Date AG verrouill\u00e9e (&le;&nbsp;6 mois).",
        "Convocation envoy\u00e9e &ge;&nbsp;J-20 + ordre du jour complet.",
        "Dossier actionnaires pr\u00eat/accessible &ge;&nbsp;J-20.",
        "PV sign\u00e9 + dossier archiv\u00e9.",
        "Ex\u00e9cution d\u00e9cisions sous 5 jours ouvr\u00e9s.",
      ]} />
    </SubAccordion>
  );
}

function FluxB() {
  return (
    <SubAccordion title={"Registre des actions, ayants droit \u00e9conomiques (ADOE)"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" tenir le registre des actions/teneurs et la liste des ayants droit \u00e9conomiques annonc\u00e9s, avec pi\u00e8ces justificatives et acc\u00e8s en Suisse."}</p>
      <SectionTitle>{"R\u00e8gles & \u00e9ch\u00e9ances"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"CO art.\u00a0697l\u00a0: obligation de tenir la liste. D\u00e9lai "}<strong>{"1 mois"}</strong>{" pour annonce sous peine d\u2019extinction de droits patrimoniaux."}</li>
        <li>{"Conservation des pi\u00e8ces "}<strong>{"10 ans apr\u00e8s radiation"}</strong>{"."}</li>
        <li>{"Fr\u00e9quence\u00a0: continu (chaque transfert) + revue annuelle."}</li>
      </ul>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-b" items={[
        "Personne domicili\u00e9e en Suisse avec acc\u00e8s op\u00e9rationnel au registre/liste.",
        "Liste ADOE \u00e0 jour (pr\u00e9nom/nom/adresse).",
        "Pi\u00e8ces conserv\u00e9es 10 ans apr\u00e8s radiation.",
        "Revue trimestrielle (registre &harr; PV &harr; capital).",
      ]} />
    </SubAccordion>
  );
}

function FluxC() {
  return (
    <SubAccordion title={"Pilotage solvabilit\u00e9, perte de capital, surendettement"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" flux \u00ab\u00a0early warning\u00a0\u00bb CA/DAF. Cash forecast 13 semaines, suivi covenants, stress tests, d\u00e9tection perte de capital/surendettement."}</p>
      <SectionTitle>{"R\u00e8gles (CO\u00a0725 ss)"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"Art.\u00a0725\u00a0: menace d\u2019insolvabilit\u00e9 \u2192 mesures \u00ab\u00a0avec c\u00e9l\u00e9rit\u00e9\u00a0\u00bb."}</li>
        <li>{"Art.\u00a0725a\u00a0: perte de capital \u2192 mesures + contr\u00f4le restreint par r\u00e9viseur."}</li>
        <li>{"Art.\u00a0725b\u00a0: surendettement \u2192 comptes interm\u00e9diaires \u00ab\u00a0imm\u00e9diatement\u00a0\u00bb, avis au tribunal."}</li>
      </ul>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-c" items={[
        "Cash forecast 13 semaines mis \u00e0 jour chaque semaine.",
        "Seuils d\u2019alerte document\u00e9s + escalade CA.",
        "Proc\u00e9dure \u00ab&nbsp;725/725a/725b&nbsp;\u00bb document\u00e9e.",
        "Dossier \u00ab&nbsp;comptes interm\u00e9diaires&nbsp;\u00bb pr\u00eat.",
      ]} />
    </SubAccordion>
  );
}

function FluxD() {
  return (
    <SubAccordion title={"Comptabilit\u00e9, cl\u00f4tures et archivage comptable"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" Record-to-Report\u00a0: enregistrer, justifier, cl\u00f4turer, publier/communiquer, conserver."}</p>
      <SectionTitle>{"R\u00e8gles & \u00e9ch\u00e9ances"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"CO art.\u00a0957\u00a0: personnes morales doivent tenir comptabilit\u00e9."}</li>
        <li>{"CO art.\u00a0958f\u00a0: conservation livres et pi\u00e8ces "}<strong>{"10 ans"}</strong>{"."}</li>
        <li>{"Cl\u00f4ture mensuelle J+5 ouvr\u00e9s\u00a0; cl\u00f4ture annuelle J+20 \u00e0 J+90."}</li>
      </ul>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-d" items={[
        "Cut-off mensuel J+5 (banque, paie, TVA).",
        "Dossier cl\u00f4ture standardis\u00e9 + PBC list.",
        "Politique d\u2019archivage 10 ans appliqu\u00e9e + PDF/A.",
        "Rapprochement banque mensuel (camt + lettrage).",
      ]} />
    </SubAccordion>
  );
}

function FluxE() {
  return (
    <SubAccordion title={"R\u00e9vision l\u00e9gale (contr\u00f4le restreint)"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" r\u00e9vision annuelle des comptes (contr\u00f4le restreint). Opting-out "}<strong>{"inapplicable \u00e0 ~20 salari\u00e9es"}</strong>{"."}</p>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-e" items={[
        "Type de contr\u00f4le valid\u00e9 et document\u00e9 (restreint).",
        "Planning audit cal\u00e9 \u00e0 T-10 semaines avant AG.",
        "PBC list compl\u00e8te et versionn\u00e9e.",
        "Rapport de r\u00e9vision pr\u00eat avant J-20 (dossier AG).",
      ]} />
    </SubAccordion>
  );
}

function FluxF() {
  return (
    <SubAccordion title={"Imp\u00f4t \u00e0 la source (Gen\u00e8ve)"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" calcul, pr\u00e9l\u00e8vement sur salaire, transmission mensuelle, reversement, gestion \u00e9v\u00e9nements."}</p>
      <SectionTitle>{"R\u00e8gles & \u00e9ch\u00e9ances"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"Reversement mensuel\u00a0: "}<strong>{"dans les 30 jours"}</strong>{" suivant le versement du salaire."}</li>
        <li>{"Solde annuel\u00a0: "}<strong>{"au plus tard le 31 janvier"}</strong>{"."}</li>
        <li>{"Annonce d\u2019engagement / fin d\u2019activit\u00e9\u00a0: "}<strong>{"dans les 8 jours"}</strong>{"."}</li>
        <li>{"Attestation-quittance\u00a0: "}<strong>{"au plus tard le 31 janvier N+1"}</strong>{"."}</li>
        <li>{"Transmission \u00e9lectronique\u00a0: ELM (Swissdec)."}</li>
      </ul>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-f" items={[
        "Bar\u00e8mes/commune de travail contr\u00f4l\u00e9s avant cl\u00f4ture paie.",
        "Transmission + paiement &le;&nbsp;30 jours apr\u00e8s versement.",
        "Entr\u00e9es/sorties annonc\u00e9es &le;&nbsp;8 jours.",
        "Attestation-quittance &le;&nbsp;31 janvier.",
        "Rapprochement mensuel IS (compta &harr; d\u00e9clarations &harr; banque).",
      ]} />
    </SubAccordion>
  );
}

function FluxG() {
  return (
    <SubAccordion title={"D\u00e9clarations sociales OCAS (AVS/AI/APG/AC) et allocations familiales"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" annoncer/tenir \u00e0 jour l\u2019estimation de masse salariale, d\u00e9clarer les salaires nominativement en fin d\u2019ann\u00e9e."}</p>
      <SectionTitle>{"R\u00e8gles & \u00e9ch\u00e9ances"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"D\u00e9claration via AVSeasy ou ELM\u00a0; d\u00e9lai "}<strong>{"\u2264\u00a030 janvier"}</strong>{"."}</li>
        <li>{"Int\u00e9r\u00eat moratoire\u00a0: "}<strong>{"5\u00a0%"}</strong>{" si d\u00e9claration tardive et solde d\u00fb."}</li>
        <li>{"Ajustement masse salariale\u00a0: annoncer toute variation \u2265\u00a020\u00a0% et \u2265\u00a020\u00a0000\u00a0CHF."}</li>
      </ul>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-g" items={[
        "Tableau de pilotage masse salariale tenu (r\u00e9el vs estim\u00e9).",
        "N\u00b0 AVS collect\u00e9s et v\u00e9rifi\u00e9s.",
        "D\u00e9claration OCAS transmise &le;&nbsp;30 janvier + quittance archiv\u00e9e.",
        "Rapprochement masse salariale (paie &harr; compta &harr; OCAS).",
      ]} />
    </SubAccordion>
  );
}

function FluxH() {
  return (
    <SubAccordion title={"Certificat de salaire annuel & gestion des frais"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" produire les certificats de salaire coh\u00e9rents avec la paie et la comptabilit\u00e9."}</p>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-h" items={[
        "R\u00e8glement de frais formalis\u00e9 (et valid\u00e9 si d\u00e9passement plafonds).",
        "Certificats g\u00e9n\u00e9r\u00e9s + contr\u00f4le coh\u00e9rence paie/compta.",
        "Consentement si transmission \u00e0 l\u2019AFC Gen\u00e8ve.",
        "Plan de correction (proc\u00e9dure certificat rectificatif).",
      ]} />
    </SubAccordion>
  );
}

function FluxI() {
  return (
    <SubAccordion title={"Accidents, maladies professionnelles, LAA et sinistres"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" d\u00e9claration des sinistres \u00e0 l\u2019assureur-accidents. Annonce ouverture/fermeture dans les "}<strong>{"14 jours"}</strong>{"."}</p>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-i" items={[
        "Proc\u00e9dure \u00ab&nbsp;accident&nbsp;\u00bb \u00e9crite + r\u00f4les connus.",
        "Police LAA active + primes correctement imput\u00e9es.",
        "D\u00e9claration assureur faite le jour m\u00eame si possible.",
        "Coordination paie/indemnit\u00e9s document\u00e9e.",
        "Dossier archiv\u00e9. Donn\u00e9es m\u00e9dicales prot\u00e9g\u00e9es (LPD).",
      ]} />
    </SubAccordion>
  );
}

function FluxJ() {
  return (
    <SubAccordion title={"TVA mensuelle"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" factures conformes, piste TVA, d\u00e9compte via ePortal, paiement, corrections annuelles. Taux\u00a0: "}<strong>{"8,1\u00a0% normal / 2,6\u00a0% r\u00e9duit / 3,8\u00a0% h\u00e9bergement"}</strong>{"."}</p>
      <SectionTitle>{"R\u00e8gles & \u00e9ch\u00e9ances"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"Remise d\u00e9compte\u00a0: "}<strong>{"60 jours"}</strong>{" apr\u00e8s fin de p\u00e9riode fiscale."}</li>
        <li>{"Paiement\u00a0: dans les "}<strong>{"60 jours"}</strong>{"."}</li>
        <li>{"Correction d\u2019erreurs\u00a0: d\u00e9compte rectificatif \u2264\u00a0"}<strong>{"180 jours"}</strong>{" apr\u00e8s fin d\u2019exercice."}</li>
      </ul>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-j" items={[
        "D\u00e9compte remis &le;&nbsp;60 jours.",
        "Paiement trac\u00e9 + preuve archiv\u00e9e.",
        "R\u00e9conciliation annuelle TVA effectu\u00e9e + rectificatif si besoin.",
      ]} />
    </SubAccordion>
  );
}

function FluxK() {
  return (
    <SubAccordion title={"Imp\u00f4t personnes morales (Gen\u00e8ve) et acomptes"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" d\u00e9claration fiscale, gestion d\u00e9lais/rappels/taxation d\u2019office. Logiciel\u00a0: GeTax soci\u00e9t\u00e9s."}</p>
      <SectionTitle>{"R\u00e8gles & \u00e9ch\u00e9ances"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"Si cl\u00f4ture 31.12\u00a0: d\u00e9p\u00f4t "}<strong>{"avant le 30 avril"}</strong>{" (prolongation payante)."}</li>
        <li>{"Acomptes mensuels\u00a0: \u00e9ch\u00e9ances au "}<strong>{"10"}</strong>{" de chaque mois, d\u00e9lai de paiement 1 mois."}</li>
        <li>{"Risque\u00a0: int\u00e9r\u00eats moratoires + taxation d\u2019office."}</li>
      </ul>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-k" items={[
        "Dossier fiscal pr\u00eat avant le 30 avril.",
        "D\u00e9lai demand\u00e9 si n\u00e9cessaire (et budg\u00e9t\u00e9).",
        "Acomptes pay\u00e9s selon calendrier + suivi int\u00e9r\u00eats.",
        "Dossier fiscal archiv\u00e9 (pi\u00e8ces + calculs).",
      ]} />
    </SubAccordion>
  );
}

function FluxL() {
  return (
    <SubAccordion title={"Tr\u00e9sorerie & paiements bancaires (pain.001/camt)"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" \u00e9mission ordres de paiement, s\u00e9curisation signatures (4-eyes), r\u00e9ception retours, rapprochement."}</p>
      <SectionTitle>{"Formats & proc\u00e9dure"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"pain.001\u00a0: MsgId, EndToEndId unique et tra\u00e7able, double validation."}</li>
        <li>{"camt.052 (intraday), camt.053 (relev\u00e9), camt.054 (avis d\u00e9bit/cr\u00e9dit)."}</li>
        <li>{"Contr\u00f4le IBAN b\u00e9n\u00e9ficiaires + rapprochement \u2264\u00a048\u00a0h."}</li>
      </ul>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-l" items={[
        "Validation 4-eyes + liste b\u00e9n\u00e9ficiaires verrouill\u00e9e.",
        "EndToEndId unique et tra\u00e7able.",
        "pain.002 archiv\u00e9.",
        "Import camt + lettrage sous 48&nbsp;h.",
      ]} />
    </SubAccordion>
  );
}

function FluxM() {
  return (
    <SubAccordion title={"Facturation clients (QR-facture) & compta clients"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" cr\u00e9er facture, envoyer, encaisser, lettrer, produire la preuve."}</p>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-m" items={[
        "Champs QR corrects (r\u00e9f\u00e9rence/IBAN).",
        "QR-facture test\u00e9e (scan).",
        "Lettrage encaissement &le;&nbsp;48&nbsp;h apr\u00e8s camt.054.",
        "DSO suivi mensuellement.",
      ]} />
    </SubAccordion>
  );
}

function FluxN() {
  return (
    <SubAccordion title={"Achats & d\u00e9penses fournisseurs (Procure-to-Pay)"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" DA \u2192 commande \u2192 r\u00e9ception \u2192 facture fournisseur \u2192 validation \u2192 paiement \u2192 lettrage \u2192 archivage."}</p>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-n" items={[
        "3-way match pour d\u00e9penses significatives.",
        "Contr\u00f4le IBAN (changement = validation renforc\u00e9e).",
        "Paiements valid\u00e9s en double signature.",
        "TVA contr\u00f4l\u00e9e (taux/mention).",
        "Dossier fournisseur archiv\u00e9 (contrat + factures).",
      ]} />
    </SubAccordion>
  );
}

function FluxO() {
  return (
    <SubAccordion title={"Notes de frais"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" soumission, contr\u00f4le, remboursement. R\u00e8glement de frais agr\u00e9\u00e9 n\u00e9cessaire si d\u00e9passement plafonds."}</p>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-o" items={[
        "Politique frais \u00e9crite et diffus\u00e9e + plafonds.",
        "Justificatifs complets (motif, participants si repas).",
        "V\u00e9rif d\u00e9passement plafonds \u2192 r\u00e8glement agr\u00e9\u00e9 si n\u00e9cessaire.",
        "Contr\u00f4le doublons automatis\u00e9 si volume.",
        "Audit anti-fraude (\u00e9chantillon mensuel).",
      ]} />
    </SubAccordion>
  );
}

function FluxP() {
  return (
    <SubAccordion title={"Immobilisations & amortissements"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" acheter \u2192 enregistrer dans registre \u2192 amortir \u2192 g\u00e9rer cession/mise au rebut."}</p>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-p" items={[
        "Registre immobilisations \u00e0 jour + inventaire annuel document\u00e9.",
        "Amortissements coh\u00e9rents (dur\u00e9es document\u00e9es).",
        "Pi\u00e8ces archiv\u00e9es 10 ans.",
        "Rapprochement registre &harr; bilan.",
      ]} />
    </SubAccordion>
  );
}

function FluxQ() {
  return (
    <SubAccordion title={"Dividendes & imp\u00f4t anticip\u00e9 (IA)"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" apr\u00e8s d\u00e9cision AG, verser le dividende et g\u00e9rer l\u2019imp\u00f4t anticip\u00e9 (35\u00a0%)."}</p>
      <SectionTitle>{"R\u00e8gles & \u00e9ch\u00e9ances"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"Formulaires \u00e0 remettre "}<strong>{"\u2264\u00a030 jours"}</strong>{" apr\u00e8s l\u2019\u00e9ch\u00e9ance de la prestation imposable."}</li>
        <li>{"D\u00e9claration obligatoire IA\u00a0: d\u00e9p\u00f4t spontan\u00e9 "}<strong>{"dans les 30 jours"}</strong>{" apr\u00e8s approbation du compte annuel."}</li>
      </ul>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-q" items={[
        "PV AG fixe clairement date d\u2019\u00e9ch\u00e9ance.",
        "Analyse IA faite avant paiement aux actionnaires.",
        "D\u00e9claration IA d\u00e9pos\u00e9e &le;&nbsp;30 jours.",
        "Dossier complet archiv\u00e9 (PV, calcul, preuve ePortal).",
      ]} />
    </SubAccordion>
  );
}

function FluxR() {
  return (
    <SubAccordion title={"Conservation/archivage & preuve + protection des donn\u00e9es (LPD)"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" comptable\u00a0: conservation "}<strong>{"10 ans"}</strong>{". Donn\u00e9es personnelles\u00a0: proportionnalit\u00e9 (LPD), registre des traitements, notification violations \u00ab\u00a0dans les meilleurs d\u00e9lais\u00a0\u00bb. Peine maximale CHF\u00a0250\u00a0000."}</p>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-r" items={[
        "Matrice de conservation publi\u00e9e (compta 10 ans).",
        "Registre LPD tenu et \u00e0 jour + exceptions document\u00e9es.",
        "Playbook incident \u00ab&nbsp;violation de donn\u00e9es&nbsp;\u00bb pr\u00eat.",
        "Archivage comptable 10 ans + PDF/A pour documents finaux.",
      ]} />
    </SubAccordion>
  );
}

function FluxS() {
  return (
    <SubAccordion title={"Recrutement, permis de travail, onboarding"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" Hire-to-Onboard. C\u2019est "}<strong>{"l\u2019employeur"}</strong>{" qui effectue les d\u00e9marches de permis (OCPM). \u00c9tat tiers\u00a0: autorisations ~"}<strong>{"6\u20138 semaines"}</strong>{" avant entr\u00e9e."}</p>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-s" items={[
        "Statut et fili\u00e8re (UE/AELE / \u00c9tat tiers) identifi\u00e9s.",
        "V\u00e9rif droit au travail (type de permis).",
        "Dossier permis complet avant date d\u2019entr\u00e9e.",
        "Param\u00e9trage IS + ELM (si soumis).",
      ]} />
    </SubAccordion>
  );
}

function FluxT() {
  return (
    <SubAccordion title={"Paie mensuelle (A3)"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" cycle mensuel\u00a0: collecter variables \u2192 calcul brut/net \u2192 paiements \u2192 \u00e9critures comptables \u2192 reporting."}</p>
      <SectionTitle>{"Proc\u00e9dure"}</SectionTitle>
      <ul className="list-disc pl-5 mb-3 space-y-1">
        <li>{"J-7\u00a0: collecter variables + absences."}</li>
        <li>{"J-6\u00a0: pr\u00e9-calcul, contr\u00f4les (\u00e9carts N-1, \u00e9carts vs budget)."}</li>
        <li>{"J-3\u00a0: validation DAF, g\u00e9n\u00e9ration fichier paiement."}</li>
        <li>{"J-2\u00a0: ex\u00e9cution bancaire + comptabilisation."}</li>
      </ul>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-t" items={[
        "Cut-off variables J-5 ouvr\u00e9s.",
        "Contr\u00f4le 4 yeux sur net/paiements.",
        "Contr\u00f4le \u00e9carts (N-1, budget, anomalies).",
        "Export paiements + \u00e9critures paie archiv\u00e9s.",
      ]} />
    </SubAccordion>
  );
}

function FluxV() {
  return (
    <SubAccordion title={"LPP (2\u1d49 pilier)"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" affiliation, entr\u00e9es/sorties, contr\u00f4le salaire assur\u00e9, paiement primes. Salari\u00e9es assur\u00e9es si salaire annuel \u2265\u00a0"}<strong>{"22\u00a0680\u00a0CHF"}</strong>{" (2026)."}</p>
      <SectionTitle>Checklist</SectionTitle>
      <Checklist tab={TAB} idPrefix="flux-v" items={[
        "Param\u00e8tres 2026 appliqu\u00e9s (seuils).",
        "Entr\u00e9es/sorties annonc\u00e9es &le;&nbsp;30 jours (cible interne).",
        "Rapprochement primes &harr; paie.",
        "Archives (certificats + confirmations).",
      ]} />
    </SubAccordion>
  );
}

function FluxW() {
  return (
    <SubAccordion title={"Redevance radio-TV des entreprises"}>
      <p className="mb-3"><strong>{"Description\u00a0:"}</strong>{" due si assujetti TVA et CA mondial atteint le seuil (bar\u00e8me par tranches). Facturation annuelle."}</p>
      <SectionTitle>Risques</SectionTitle>
      <ul className="list-disc pl-5 mb-0 space-y-1">
        <li>{"Rappels/recouvrement si impay\u00e9."}</li>
        <li>{"Charge non budg\u00e9t\u00e9e si omission."}</li>
      </ul>
    </SubAccordion>
  );
}

function FluxX() {
  return (
    <SubAccordion title={"Obligations conditionnelles (mati\u00e8res premi\u00e8res / ODiTr / ESG)"}>
      <p className="mb-3"><strong>{"Transparence \u00ab\u00a0paiements aux gouvernements\u00a0\u00bb\u00a0:"}</strong>{" si contr\u00f4le ordinaire + extraction (minerais/p\u00e9trole/gaz)\u00a0: rapport annuel, publication \u2264\u00a06 mois, accessibilit\u00e9 10 ans (CO art.\u00a0964a\u2013964d)."}</p>
      <p className="mb-3"><strong>{"ODiTr\u00a0:"}</strong>{" si minerais/m\u00e9taux zones de conflit ou travail des enfants\u00a0: devoirs de diligence + rapport p\u00e9riodique annuel. Risque p\u00e9nal/administratif."}</p>
      <p className="mb-0 p-3 bg-[#eef4ef] dark:bg-[rgba(46,109,59,0.18)] rounded-lg border border-[#bfd1c3] dark:border-[#2e6d3b] text-[#3f6947] dark:text-[#4d8a5b]">
        <strong>{"Point cl\u00e9\u00a0:"}</strong>{" sans donn\u00e9es pr\u00e9cises (approvisionnements, produits, seuils), impossible de trancher si la SA est concern\u00e9e. Bloc conditionnel \u00e0 qualifier."}
      </p>
    </SubAccordion>
  );
}
